import { Link } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { useActiveJobsStore, type ActiveJob } from "@/stores/active-jobs-store"
import { documentJobLine } from "@/lib/document-job-progress"
import { buildSectionLink } from "@/lib/active-job-link"
import { BOXED_ROW_CLASS, BoxedList, SectionHeader } from "@/components/dashboard/DashboardSection"

function isLive(job: ActiveJob): boolean {
  return job.status === "pending" || job.status === "running"
}

function progressLine(job: ActiveJob): string {
  if (job.jobType === "document_job") return documentJobLine(job)
  return job.progressMessage ?? (job.status === "pending" ? "Waiting to start" : "Working")
}

function JobRowContent({ job }: { job: ActiveJob }) {
  return (
    <>
      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[hsl(var(--ai-accent))]" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium">{job.title ?? "AI task"}</p>
        <p className="text-meta mt-0.5 truncate">{progressLine(job)}</p>
      </div>
    </>
  )
}

// Home's "Working on it": AI jobs still running or waiting, from the same store
// as the header's tray (which also polls it), linking where the tray links.
// Hidden entirely when nothing is running.
export function WorkingOnIt() {
  const jobs = useActiveJobsStore((s) => s.jobs)
  const live = jobs.filter(isLive)
  if (live.length === 0) return null

  return (
    <section data-testid="working-on-it">
      <SectionHeader title="Working on it" />
      <BoxedList>
        {live.map((job) => {
          const href = buildSectionLink(job)
          const rowClass = cn("flex items-center gap-3", BOXED_ROW_CLASS)
          return href ? (
            <Link key={job.jobId} to={href} className={rowClass} data-testid="working-on-it-row">
              <JobRowContent job={job} />
            </Link>
          ) : (
            <div key={job.jobId} className={cn("flex items-center gap-3 px-4 py-2.5")} data-testid="working-on-it-row">
              <JobRowContent job={job} />
            </div>
          )
        })}
      </BoxedList>
    </section>
  )
}
